/**
 * Analytics Event Factory Functions
 *
 * Each function constructs a typed dataLayer event payload.
 * Functions accept pre-extracted data (not store references) for testability.
 */

import type {
  AnalyticsParameters,
  AnalyticsItem,
  ViewItemListEvent,
  ViewItemEvent,
  BookingCheckSuccessEvent,
  BookingCheckFailEvent,
  BeginCheckoutEvent,
  CheckoutInsuranceEvent,
  CheckoutDataEvent,
  CheckoutPaymentEvent,
  AddPaymentInfoEvent,
  PurchaseEvent,
  BookingErrorEvent,
  HolidaySelectedEvent,
} from './types';
import { pushToDataLayer, pushWithCallback } from './dataLayer';
import type { DataLayerEvent, EventCallback } from './dataLayer';

// GA cannot read nested JSON in custom event params (only `items` is supported).
// Serialize AnalyticsParameters into a flat string instead.
const PARAM_KEY_MAP: Record<keyof AnalyticsParameters, string> = {
  destination: 'destination',
  date_from: 'date-from',
  date_to: 'date-to',
  duration: 'duration',
  adults: 'adults',
  children: 'children',
  rooms: 'rooms',
  board_code: 'board-code',
};

function serializeParameters(params: AnalyticsParameters): string {
  return (Object.keys(params) as Array<keyof AnalyticsParameters>)
    .filter((key) => params[key] !== undefined)
    .map((key) => `${PARAM_KEY_MAP[key]}: ${params[key]}`)
    .join(', ');
}

// ============================================
// Event Constructors (pure functions, no side effects)
// ============================================

export function createViewItemListEvent(params: AnalyticsParameters): ViewItemListEvent {
  return {
    event: 'view_item_list',
    parameters: serializeParameters(params),
  };
}

export function createViewItemEvent(
  params: AnalyticsParameters,
  items: AnalyticsItem[]
): ViewItemEvent {
  return {
    event: 'view_item',
    parameters: serializeParameters(params),
    items,
  };
}

export function createBookingCheckSuccessEvent(data: {
  session: string | null;
  params: AnalyticsParameters;
  davinciCode: number;
  bookingWarning?: string;
}): BookingCheckSuccessEvent {
  return {
    event: 'booking_check_success',
    session: data.session,
    parameters: serializeParameters(data.params),
    davinci_code: data.davinciCode,
    ...(data.bookingWarning && { booking_warning: data.bookingWarning }),
  };
}

export function createBookingCheckFailEvent(data: {
  session: string | null;
  params: AnalyticsParameters;
  davinciCode: number;
  errorText: string;
  bookingWarning?: string;
}): BookingCheckFailEvent {
  return {
    event: 'booking_check_fail',
    session: data.session,
    parameters: serializeParameters(data.params),
    davinci_code: data.davinciCode,
    error_text: data.errorText,
    ...(data.bookingWarning && { booking_warning: data.bookingWarning }),
  };
}

export function createBeginCheckoutEvent(data: {
  session: string | null;
  params: AnalyticsParameters;
  currency: string;
  value: number;
  bookingWarning?: string;
  items: AnalyticsItem[];
}): BeginCheckoutEvent {
  return {
    event: 'begin_checkout',
    session: data.session,
    parameters: serializeParameters(data.params),
    currency: data.currency,
    value: data.value,
    ...(data.bookingWarning && { booking_warning: data.bookingWarning }),
    items: data.items,
  };
}

export function createCheckoutInsuranceEvent(data: {
  session: string | null;
  productId: string | null;
  params: AnalyticsParameters;
}): CheckoutInsuranceEvent {
  return {
    event: 'checkout_2_insurance',
    session: data.session,
    productId: data.productId,
    parameters: serializeParameters(data.params),
  };
}

export function createCheckoutDataEvent(data: {
  session: string | null;
  productId: string | null;
  params: AnalyticsParameters;
}): CheckoutDataEvent {
  return {
    event: 'checkout_3_data',
    session: data.session,
    productId: data.productId,
    parameters: serializeParameters(data.params),
  };
}

export function createCheckoutPaymentEvent(data: {
  session: string | null;
  productId: string | null;
  params: AnalyticsParameters;
  bookingWarning?: string;
}): CheckoutPaymentEvent {
  return {
    event: 'checkout_4_payment',
    session: data.session,
    productId: data.productId,
    parameters: serializeParameters(data.params),
    ...(data.bookingWarning && { booking_warning: data.bookingWarning }),
  };
}

export function createAddPaymentInfoEvent(data: {
  session: string | null;
  paymentType: string;
  productId: string | null;
  params: AnalyticsParameters;
}): AddPaymentInfoEvent {
  return {
    event: 'add_payment_info',
    session: data.session,
    payment_type: data.paymentType,
    productId: data.productId,
    parameters: serializeParameters(data.params),
  };
}

export function createPurchaseEvent(data: {
  session: string | null;
  currency: string;
  value: number;
  params: AnalyticsParameters;
  bookingWarning?: string;
  transactionId: string;
  items: AnalyticsItem[];
}): PurchaseEvent {
  return {
    event: 'purchase',
    session: data.session,
    currency: data.currency,
    value: data.value,
    parameters: serializeParameters(data.params),
    ...(data.bookingWarning && { booking_warning: data.bookingWarning }),
    transaction_id: data.transactionId,
    items: data.items,
  };
}

export function createBookingErrorEvent(data: {
  session: string | null;
  davinciCode: number;
  errorText: string;
  params: AnalyticsParameters;
}): BookingErrorEvent {
  return {
    event: 'booking_error',
    session: data.session,
    davinci_code: data.davinciCode,
    error_text: data.errorText,
    parameters: serializeParameters(data.params),
  };
}

export function createHolidaySelectedEvent(params: AnalyticsParameters): HolidaySelectedEvent {
  return {
    event: 'holiday_selected',
    parameters: serializeParameters(params),
  };
}

// ============================================
// Push Helpers (side-effecting: actually push to dataLayer)
// ============================================

/** Push a standard event (no redirect delay) */
export function pushEvent(event: DataLayerEvent): void {
  pushToDataLayer(event);
}

/** Push an event that must complete before redirect */
export function pushEventBeforeRedirect(
  event: DataLayerEvent,
  callback: EventCallback,
  timeoutMs?: number
): void {
  pushWithCallback(event, callback, timeoutMs);
}
